import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Timeline as TimelineIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { macroAPI } from '../../services/api';

const PillarTrendAnalysis = () => {
  const { t } = useTranslation();
  const [trends, setTrends] = useState([]);
  const [pillarNames, setPillarNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(90);

  useEffect(() => {
    fetchTrends(timeRange);
  }, [timeRange]);

  const fetchTrends = async (days) => {
    try {
      setLoading(true);
      const response = await macroAPI.getPillarTrends(days);
      setTrends(response.data.trends || []);
      setPillarNames(response.data.pillar_names || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pillar trends:', error);
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (event, newRange) => {
    if (newRange !== null) {
      setTimeRange(newRange);
    }
  };

  // Transform data for chart
  const chartData = trends.map(trend => {
    const dataPoint = {
      date: new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    };
    
    Object.keys(trend.pillars).forEach(pillarCode => {
      dataPoint[pillarCode] = trend.pillars[pillarCode].score;
    });
    
    return dataPoint;
  });

  // Pillar colors
  const pillarColors = {
    'A': '#11998e',
    'B': '#667eea',
    'C': '#f093fb',
    'D': '#4facfe',
    'E': '#43e97b',
    'F': '#fa709a',
    'G': '#feca57',
    'H': '#ff6b6b',
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            p: 2,
            borderRadius: 2,
            boxShadow: 3,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {pillarNames[entry.dataKey]?.split('/')[0] || entry.dataKey}: <strong>{entry.value.toFixed(1)}</strong>
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TimelineIcon sx={{ fontSize: 32, mr: 1, color: '#667eea' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('dashboard.pillarTrend.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.pillarTrend.subtitle')}
              </Typography>
            </Box>
          </Box>
          
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={handleTimeRangeChange}
            size="small"
          >
            <ToggleButton value={30}>30D</ToggleButton>
            <ToggleButton value={90}>90D</ToggleButton>
            <ToggleButton value={180}>180D</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {trends.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t('dashboard.pillarTrend.noData')}
            </Typography>
          </Box>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={400} minWidth={400} minHeight={400}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  stroke="#666"
                  style={{ fontSize: '0.75rem' }}
                />
                <YAxis
                  stroke="#666"
                  style={{ fontSize: '0.75rem' }}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} animationDuration={50} animationEasing="ease-out" />
                <Legend
                  wrapperStyle={{ fontSize: '0.75rem' }}
                  formatter={(value) => pillarNames[value]?.split('/')[0] || value}
                />
                {Object.keys(pillarColors).map(pillarCode => (
                  <Line
                    key={pillarCode}
                    type="monotone"
                    dataKey={pillarCode}
                    stroke={pillarColors[pillarCode]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>

            {/* Pillar Legend with Names */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              {Object.keys(pillarColors).map(pillarCode => (
                <Chip
                  key={pillarCode}
                  label={`${pillarCode}: ${pillarNames[pillarCode]?.split('/')[0] || pillarCode}`}
                  size="small"
                  sx={{
                    bgcolor: pillarColors[pillarCode],
                    color: 'white',
                    fontSize: '0.7rem',
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PillarTrendAnalysis;

